/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { membersGridDataRefreshAtom } from './state';

interface InviteMemberModalProps {
  isOpen: boolean;
  toggle: () => void;
  userData?: { first_name: string; last_name: string; email: string };
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  toggle,
  userData,
}) => {
  const [firstName, setFirstName] = useState(userData?.first_name || '');
  const [lastName, setLastName] = useState(userData?.last_name || '');
  const [email, setEmail] = useState(userData?.email || '');
  const [loading, setLoading] = useState(false);
  const companyId = useAtomValue(companyIdAtom);
  const membersGridDataRefresh = useSetAtom(membersGridDataRefreshAtom);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName || !lastName || !email) {
      showFailureToast('Please fill all required fields with valid data');
      return;
    }
    setLoading(true);
    try {
      await callApi(
        '/profiles/',
        'POST',
        {},
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          email: email.trim(),
          company_id: companyId,
        },
      );

      showSuccessToast('Invitation sent successfully');
      membersGridDataRefresh(Math.random().toString());
      toggle();
    } catch (error: any) {
      console.error('Invite member error:', error);
      // Handle field-specific validation errors
      const errorData = error?.data;
      let errorMessage = 'Failed to send invitation';
      if (errorData && typeof errorData === 'object' && !errorData.detail) {
        // Handle field validation errors like {email: ["error message"]}
        const firstField = Object.keys(errorData)[0];
        if (
          firstField &&
          Array.isArray(errorData[firstField]) &&
          errorData[firstField].length > 0
        ) {
          errorMessage = errorData[firstField][0];
        }
      } else if (errorData?.detail) {
        // Handle general detail errors
        errorMessage = errorData.detail;
      }
      showFailureToast(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Prevent closing while loading
  const handleToggle = () => {
    if (!loading) toggle();
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
    >
      <ModalHeader toggle={loading ? undefined : handleToggle}>
        {userData ? 'Resend Invite' : 'Invite New Member'}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={handleSubmit} autoComplete="on">
          {/* First Name */}
          <FormGroup>
            <label>
              First Name<span className="text-danger">*</span>
            </label>
            <Input
              type="text"
              name="firstName"
              placeholder="Enter first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              invalid={firstName.trim().length === 0}
              disabled={loading}
            />
          </FormGroup>

          {/* Last Name */}
          <FormGroup>
            <label>
              Last Name<span className="text-danger">*</span>
            </label>
            <Input
              type="text"
              name="lastName"
              placeholder="Enter last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              invalid={lastName.trim().length === 0}
              disabled={loading}
            />
          </FormGroup>

          {/* Email */}
          <FormGroup>
            <label>
              Email<span className="text-danger">*</span>
            </label>
            <Input
              type="email"
              name="email"
              placeholder="Enter email address"
              autoComplete="email"
              value={email}
              invalid={
                email.trim().length === 0 || !emailRegex.test(email.trim())
              }
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleToggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={
            loading ||
            !firstName ||
            !lastName ||
            !email ||
            !emailRegex.test(email.trim())
          }
        >
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" /> Sending…
            </>
          ) : (
            'Send Invite'
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InviteMemberModal;
